import { CloseCircleIcon, InfoIcon } from '@mm/icons';
import {
  Heading,
  Tooltip,
  KeywordsBroadMatchTable,
  ErrorAlert,
} from '@mm/components';
import React, { useState, Dispatch, SetStateAction } from 'react';
import { words } from 'lodash';
import { useSelector } from 'react-redux';
import closeIcon from 'assets/icons/close.svg';
import classnames from 'classnames';
import { ENTER_KEY_NAME, TAB_KEY_NAME } from '@mm/constants/keys-codes';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { PrimaryButton } from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import CheckBox from '../../../../../../../common/components/Checkbox';
import RadioInput from '../../../../../../../common/components/RadioInput';
import TextInput from '../../../../../../../common/components/TextInput';
import './KeywordsForm.pcss';
import { BROAD_MATCH, BROAD_MATCH_LABELS } from './BroadMatch';

const NO_KEYWORDS_WARNING =
  'Warning: Comment guard will respond to all comments unless you specify keywords.';
const CHECKBOX_LABEL = "Respond only if the user's comment contains keyword(s)";

type Props = {
  guard: PromoterModel;
  useUpdatedDesign?: boolean;
  allowPhraseKeywords?: boolean;
  setToggler?: Dispatch<SetStateAction<'all' | 'keywords'>>;
  showDisabledState?: boolean;
  showKeywordsBroadMatch?: boolean;
  keywordsWrapperClassName?: string;
  validate?: (model: Partial<PromoterModel>, func: () => void) => Promise<void>;
  inputLabel?: string;
  inputPlaceholder?: string;
  customAddBtnName?: string;
  customRespondAllName?: string;
  customRespondToCommentsName?: string;
  customKeywords?: string[];
  customDeleteKeywordIcon?: React.ReactNode;
  customAddKeywordFunc?: (keyword: string) => void;
  customDeleteKeywordFunc?: (index: number) => void;
  infoIconColor?: string;
  customKeywordBroadPosition?: 'top' | 'bottom' | 'right' | 'left';
  customKeywordBroadAlign?: 'start' | 'center' | 'end';
};

type KeywordProps = {
  name: string;
  onDelete: () => void;
  useUpdatedDesign?: boolean;
  customDeleteKeywordIcon?: React.ReactNode;
};

const Keyword = ({
  name,
  onDelete,
  useUpdatedDesign,
  customDeleteKeywordIcon,
}: KeywordProps) => (
  <div className="comment-guard-keywords__keyword">
    <div className="comment-guard-keywords__keyword-name">{name}</div>
    {customDeleteKeywordIcon ? (
      <div onClick={onDelete}>{customDeleteKeywordIcon}</div>
    ) : useUpdatedDesign ? (
      <CloseCircleIcon
        className="comment-guard-keywords__keyword-remove"
        onClick={onDelete}
        color="#009EFF"
      />
    ) : (
      <div
        className="comment-guard-keywords__keyword-remove"
        onClick={onDelete}
      >
        <img src={closeIcon} alt="" />
      </div>
    )}
  </div>
);

const useRedux = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { update } = usePromoterCRUDActions();

  const updateGuard = (guard: PromoterModel) =>
    update(chatbotId, normalizeGuardKeywords(guard));

  return { updateGuard };
};

const normalizeGuardKeywords = guard => {
  if (guard?.keywords?.length) {
    const keywords = guard.keywords
      .slice(0, -1)
      .map(k => k.replace(/(?!\w|\s)./g, ''));

    const normalizeKeywords = [...keywords, guard.keywords.pop()];

    guard.keywords = normalizeKeywords;
  }

  return guard;
};

const keywordsPhrase = (guard, customKeywords) => {
  if (guard?.phrases?.length) {
    return guard?.phrases.map(p => p.phrase);
  }

  return customKeywords ? customKeywords : guard.keywords;
};

const KeywordsForm = (props: Props) => {
  const {
    guard,
    useUpdatedDesign,
    allowPhraseKeywords,
    setToggler,
    showDisabledState,
    keywordsWrapperClassName,
    inputLabel,
    customKeywords,
    validate,
    inputPlaceholder,
    customAddBtnName,
    customRespondAllName,
    customRespondToCommentsName,
    showKeywordsBroadMatch,
    customDeleteKeywordIcon,
    customAddKeywordFunc,
    customDeleteKeywordFunc,
    infoIconColor,
    customKeywordBroadPosition,
    customKeywordBroadAlign,
  } = props;
  const [activeKeyword, setActiveKeyword] = useState<string>();
  const [keywordsEnabled, setKeywordsEnabled] = useState(
    !!guard.keywordsEnabled,
  );
  const { updateGuard } = useRedux();

  const currentKeywords = keywordsPhrase(guard, customKeywords) || [];
  const className = classnames('comment-guard-keywords', {
    'comment-guard-keywords--updated': useUpdatedDesign,
  });

  const onAddKeyword = () => {
    if (activeKeyword && activeKeyword.trim()) {
      if (
        !currentKeywords
          .map(w => w.toLowerCase())
          .includes(activeKeyword.toLowerCase())
      ) {
        if (customAddKeywordFunc) {
          customAddKeywordFunc(activeKeyword);
        } else {
          updateGuard({
            ...guard,
            keywordsEnabled: true,
            keywords: [...currentKeywords, activeKeyword.trim()],
          });
        }
      }
    }

    setActiveKeyword('');
  };

  const onDeleteKeyword = (i: number) => {
    if (customDeleteKeywordFunc) {
      customDeleteKeywordFunc(i);
      return;
    }
    const collectKeywords = [
      ...currentKeywords.slice(0, i),
      ...currentKeywords.slice(i + 1),
    ];

    const keywords = normalizeKeywords(collectKeywords);

    const payload = {
      ...guard,
      keywords,
      keywordsEnabled: !collectKeywords.length ? false : true,
    };

    if (validate) {
      validate(payload, () => {
        updateGuard(payload);
      });
    } else {
      updateGuard(payload);
    }
    if (!collectKeywords.length) {
      setKeywordsEnabled(false);
    }
  };

  const onEnableKeywordsToggle = () => {
    validate?.({ ...guard, keywordsEnabled: !keywordsEnabled }, () => {
      setKeywordsEnabled(!keywordsEnabled);

      if (keywordsEnabled) {
        updateGuard({
          ...guard,
          keywordsEnabled: false,
        });
      }
    });
  };

  const normalizeKeywords = keywords => {
    if (keywords && keywords.length) {
      return keywords.map(k => k.replace(/(?!\w|\s)./g, ''));
    }

    return keywords;
  };

  const onChange = (value: string) => {
    const keyword = allowPhraseKeywords ? value : words(value).join('');
    setActiveKeyword(keyword);
  };

  const onKeywordsInputChange = (forCatchAll: boolean) => {
    if (forCatchAll) {
      setToggler?.('all');
      setKeywordsEnabled(false);
      updateGuard({
        ...guard,
        keywords: [],
        keywordsEnabled: false,
      });
    } else {
      setToggler?.('keywords');
      setKeywordsEnabled(true);
    }
  };

  return (
    <div className={className}>
      {useUpdatedDesign && (
        <Heading
          className="comment-guard-keywords__settings-header"
          isBold={false}
          size={4}
        >
          How to respond:
        </Heading>
      )}
      <div className="comment-guard-keywords__settings-container">
        {useUpdatedDesign ? (
          <>
            <RadioInput
              className="comment-guard-keywords__settings-input"
              name="keywords"
              checked={!keywordsEnabled}
              value="false"
              label={customRespondAllName || 'Respond to all comments'}
              onClick={() => onKeywordsInputChange(true)}
            />
            <RadioInput
              className="comment-guard-keywords__settings-input"
              name="keywords"
              checked={keywordsEnabled}
              value="true"
              label={
                customRespondToCommentsName ||
                'Respond only to comments that contain the following keywords'
              }
              onClick={() => onKeywordsInputChange(false)}
            />
          </>
        ) : (
          <CheckBox
            className="comment-guard-keywords__checkbox"
            label={CHECKBOX_LABEL}
            isChecked={keywordsEnabled}
            onClick={onEnableKeywordsToggle}
          />
        )}
      </div>
      <div
        className={classnames('comment-guard-keywords__keywords-wrapper', {
          'comment-guard-keywords__keywords-wrapper--visible':
            showDisabledState || keywordsEnabled,
          'comment-guard-keywords__keywords-wrapper--disabled':
            !keywordsEnabled,
          keywordsWrapperClassName,
        })}
      >
        <div className="comment-guard-keywords__label">
          {inputLabel || 'Keywords'}
        </div>
        <div className="comment-guard-keywords__text-input-wrapper">
          <div className="comment-guard-keywords__text-input-container">
            <TextInput
              className="comment-guard-keywords__text-input"
              name="name"
              value={activeKeyword}
              onChange={e => onChange(e.target.value)}
              maxLength={50}
              onKeyDown={e => {
                if (e.key === ENTER_KEY_NAME || e.key === TAB_KEY_NAME) {
                  onAddKeyword();
                }
              }}
              autoComplete="false"
              placeholder={inputPlaceholder || 'Type a keyword'}
            />
            <PrimaryButton
              className="comment-guard-keywords__btn"
              onClick={onAddKeyword}
            >
              {customAddBtnName || '+ Add'}
            </PrimaryButton>
          </div>
          {showKeywordsBroadMatch && (
            <div className="comment-guard-keywords__broad-match">
              Keywords use Broad Match by Default
              <Tooltip
                title={
                  <KeywordsBroadMatchTable
                    isMM
                    broadMatch={BROAD_MATCH}
                    labels={BROAD_MATCH_LABELS}
                  />
                }
                placement={customKeywordBroadPosition || 'right'}
              >
                <InfoIcon
                  className="comment-guard-keywords__broad-match-info-icon"
                  color={infoIconColor}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div className="comment-guard-keywords__keywords-list">
          {currentKeywords.length > 0 ? (
            currentKeywords.map((w, i) => (
              <div
                className="comment-guard-keywords__keyword-container"
                key={w + i}
              >
                {!!i && (
                  <span className="comment-guard-keywords__keyword-or">or</span>
                )}
                <Keyword
                  key={w}
                  name={w}
                  useUpdatedDesign={useUpdatedDesign}
                  onDelete={() => onDeleteKeyword(i)}
                  customDeleteKeywordIcon={customDeleteKeywordIcon}
                />
              </div>
            ))
          ) : (
            <ErrorAlert
              message={NO_KEYWORDS_WARNING}
              className="comment-guard-keywords__error-alert"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default KeywordsForm;
